import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import tabId from 'services/tabId';
import CardDashboardService from 'components/atoms/card_dashboard_service';
import { getPendingDocumentsCount } from 'services/actions/documents_count';
import { monitorarCount } from 'services/actions/monitorar_count';
import { PanelState } from 'store/modules/panel_store/types';
import { ApplicationState } from 'store';
import { totalBiddingUnread, getPortalsExpired } from 'services/actions/panel_actions';
import { ProfileState } from 'store/modules/profile_store/types';
import BannerSlideNewPanel from 'components/molecules/banner_slide_new_panel';
import useOnlyConlicitaseg from 'hooks/useOnlyConlicitaseg';
import { totalOf, useRepresentantDashboard } from 'hooks/useRepresentantDashboard';
import { getSelectedRepresented } from 'services/selected_companies';

const DashboardSession = () => {
  const [unreadBulletins, setUnreadBulletins] = useState<number | null>(null);
  const [pendingDocuments, setPendingDocuments] = useState<number | null>(null);
  const [newMessages, setNewMessages] = useState<number | null>(null);
  const [portalsExpired, setPortalsExpired] = useState<number>(0);
  const [unreadBulletinsError, setUnreadBulletinsError] = useState<string>('');
  const [pendingDocumentsError, setPendingDocumentsError] = useState<string>('');
  const [newMessagesError, setNewMessagesError] = useState<string>('');

  const { panel } = useSelector<ApplicationState, PanelState>((state) => state.session.panel);
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const isOnlyConlicitaseg = useOnlyConlicitaseg();

  const companies = useMemo(
    () => (profile.representant ? getSelectedRepresented(profile) : []),
    [profile],
  );
  const companiesDashboard = useRepresentantDashboard(companies, profile.id);
  const multi = companiesDashboard.enabled;
  const { breakdown } = companiesDashboard;
  const countersError = (message: string) => (companiesDashboard.countersError ? message : '');

  useEffect(() => {
    if (multi) return;

    const fetchData = async () => {
      try {
        const totalUnread = await totalBiddingUnread(
          profile.representant ? profile?.currentCompany[tabId()] : profile.clientId,
        );
        setUnreadBulletins(totalUnread);
      } catch {
        setUnreadBulletinsError('Erro ao carregar boletins não lidos');
      }

      if (isOnlyConlicitaseg) return;

      try {
        const totalPending = await getPendingDocumentsCount(
          profile.representant ? profile?.currentCompany[tabId()] : profile.clientId,
        );
        setPendingDocuments(totalPending);
      } catch {
        setPendingDocumentsError('Erro ao carregar documentos pendentes');
      }

      try {
        const messages = await monitorarCount(
          profile.representant ? profile?.currentCompany[tabId()] : profile.clientId,
        );
        setNewMessages(messages.monitorar);
      } catch {
        setNewMessagesError('Erro ao carregar novas mensagens');
      }

      try {
        const value = await getPortalsExpired(
          profile.representant ? profile?.currentCompany[tabId()] : profile.clientId,
        );
        setPortalsExpired(value);
      } catch {
        setNewMessagesError('Erro buscar portais vencidos');
      }
    };

    fetchData();
  }, [multi, isOnlyConlicitaseg]);

  return (
    <>
      <h4 className="pt-3">Dashboard</h4>
      <div className="row">
        <div className="col-md-6">
          <div className="row">
            <div className="col-md-6 mt-md-0 mt-3 pr-md-2">
              <CardDashboardService
                title={multi ? 'Boletins não lidos' : 'Boletim não lido'}
                trackName="Boletim não lido"
                img="bulletins"
                value={multi ? totalOf(breakdown.boletins) : unreadBulletins || 0}
                error={
                  multi
                    ? countersError('Erro ao carregar boletins não lidos')
                    : unreadBulletinsError
                }
                breakdown={multi ? breakdown.boletins : undefined}
                link="/boletim_web/public/boletins"
                active
                source="dashboard"
              />
            </div>
            <div className="col-md-6 mt-md-0 mt-3 px-md-2">
              <CardDashboardService
                title="Documentos pendentes"
                img="folder"
                value={multi ? totalOf(breakdown.documentos) : pendingDocuments || 0}
                error={
                  multi && companiesDashboard.documentsError
                    ? 'Erro ao carregar documentos pendentes'
                    : pendingDocumentsError
                }
                breakdown={multi ? breakdown.documentos : undefined}
                link="/documentos"
                active={
                  isOnlyConlicitaseg ||
                  (multi
                    ? companiesDashboard.documentsAvailable ??
                      panel?.validate_services?.gerenciar_documentos
                    : panel?.validate_services?.gerenciar_documentos)
                }
                tool="Gerenciar Documentos"
                locked={isOnlyConlicitaseg}
                source="dashboard"
              />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-md-6 mt-md-0 mt-3 pr-md-2">
              <CardDashboardService
                title="Novas mensagens"
                img="chat"
                value={multi ? totalOf(breakdown.mensagens) : newMessages || 0}
                error={multi ? countersError('Erro ao carregar novas mensagens') : newMessagesError}
                breakdown={multi ? breakdown.mensagens : undefined}
                link="/facilitadores/monitoramento"
                active={isOnlyConlicitaseg || panel?.validate_services?.monitorar_chat}
                tool="Monitorar Chat"
                locked={isOnlyConlicitaseg}
                source="dashboard"
              />
            </div>
            <div className="col-md-6 mt-md-0 mt-3 px-md-2">
              <CardDashboardService
                title="Portais vencidos"
                img="manage_portals"
                value={multi ? totalOf(breakdown.portais) : portalsExpired}
                error={multi ? countersError('Erro ao buscar portais vencidos') : newMessagesError}
                breakdown={multi ? breakdown.portais : undefined}
                link="/portais"
                active
                tool="Portais vencidos"
                locked={isOnlyConlicitaseg}
                source="dashboard"
              />
            </div>
          </div>
        </div>
        <div className="col-md-6 mt-3 mt-md-0">
          <BannerSlideNewPanel />
        </div>
      </div>
    </>
  );
};

export default DashboardSession;
